import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "../lib/api";
import RevealText from "../components/RevealText";
import Marquee from "../components/Marquee";
import ArtworkCard from "../components/ArtworkCard";
import Seo, { usePageSeo } from "../lib/seo";

export default function Home() {
  const [works, setWorks] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [genesis, setGenesis] = useState([]);
  const heroRef = useRef(null);
  const pageSeo = usePageSeo("home");

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  useEffect(() => {
    api.get("/artworks").then((r) => setWorks(r.data)).catch(() => {});
    api.get("/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/artworks", { params: { collection: "genesis" } }).then((r) => setGenesis(r.data)).catch(() => {});
  }, []);

  const nonGenesis = works.filter((w) => w.collection !== "genesis");
  const homeSelected = nonGenesis.filter((w) => w.home_archive);
  const homeWorks = (homeSelected.length ? homeSelected : nonGenesis).slice(0, 6);
  const hero = featured[0] || nonGenesis[0] || works[0];
  const genesisWork = genesis[0];

  return (
    <main data-testid="home-page">
      <Seo title={pageSeo?.seo_title} description={pageSeo?.seo_description}
        canonical={`${window.location.origin}/`} image={hero?.eff_og_image} />
      {/* HERO STATEMENT */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-40 md:pt-56 pb-20 md:pb-32">
        <motion.div
          className="label mb-10 md:mb-16"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }}
        >
          RCKT — Ongoing Archive
        </motion.div>
        <RevealText
          lines={["IT'S ALL", "A RCKT."]}
          className="font-display font-800 uppercase leading-[0.86] tracking-[-0.02em] text-[19vw] md:text-[15vw] lg:text-[13vw]"
        />
      </section>

      {/* HERO ARTWORK */}
      <section ref={heroRef} className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-40">
        {hero && (
          <Link to={`/archive/${hero.slug}`} className="group block relative" data-testid="hero-artwork">
            <div className="overflow-hidden bg-white border hairline">
              <motion.img
                src={hero.image}
                alt={`${hero.number_label} — ${hero.title}`}
                style={{ y, scale }}
                className="w-full h-[62vh] md:h-[86vh] object-cover"
              />
            </div>
            <div className="mt-5 flex items-baseline justify-between">
              <span className="label">{hero.number_label} — {hero.title}</span>
              <span className="label">{hero.medium}</span>
            </div>
          </Link>
        )}
      </section>

      {/* ARCHIVE INTRO + GRID */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-end justify-between border-b hairline pb-6 mb-14">
          <h2 className="font-display font-700 uppercase tracking-[-0.01em] text-4xl md:text-6xl">Archive</h2>
          <div className="text-right">
            <div className="label">RCKT 001 — 1000</div>
            <Link to="/archive" className="label mt-2 inline-block hover:opacity-50 transition-opacity" data-testid="home-view-archive">View all →</Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {homeWorks.map((a, i) => (
            <ArtworkCard key={a.slug} art={a} index={i} />
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <section className="mt-24 md:mt-40">
        <Marquee items={Array(4).fill("A MARK IS NEVER JUST A MARK.")} separator=" " />
      </section>

      {/* GENESIS */}
      {genesisWork && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-24 md:pt-40">
          <div className="flex items-end justify-between border-b hairline pb-6 mb-14">
            <h2 className="font-display font-700 uppercase tracking-[-0.01em] text-4xl md:text-6xl">Genesis</h2>
            <div className="label max-w-[16rem] text-right">Before the numbering began</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <Link to={`/archive/${genesisWork.slug}`} className="md:col-span-7 group block overflow-hidden bg-white border hairline" data-testid="home-genesis-artwork">
              <img src={genesisWork.image} alt={genesisWork.number_label} className="art-img w-full h-[70vh] object-cover" loading="lazy" />
            </Link>
            <div className="md:col-span-4 md:col-start-9">
              <div className="label mb-3">{genesisWork.number_label}</div>
              <div className="font-display font-700 text-4xl md:text-5xl uppercase">{genesisWork.title}</div>
              <div className="text-[var(--muted)] mt-4">{genesisWork.year}</div>
              <p className="mt-6 text-[var(--muted)] leading-relaxed max-w-sm">{genesisWork.description}</p>
              <Link to="/archive" className="label mt-8 inline-block hover:opacity-50 transition-opacity" data-testid="home-view-genesis">View archive →</Link>
            </div>
          </div>
        </section>
      )}

      {/* ABOUT SHORT */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-32 md:pt-48">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-2 label pt-3">About RCKT</div>
          <div className="md:col-span-8">
            <p className="font-display font-500 text-2xl md:text-4xl leading-[1.2] tracking-[-0.01em]">
              RCKT is an ongoing body of work exploring writing, marks, repetition and form: a single
              language pursued across one thousand numbered works.
            </p>
            <Link to="/about" className="label mt-10 inline-block hover:opacity-50 transition-opacity" data-testid="home-read-about">
              Read the statement →
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
